import axios from 'axios'

// import { Account, Balance, Claims } from '../wallet'
import { getScriptHashFromAddress, getAddressFromScriptHash, Balance } from '../wallet'
import { Transaction } from '../transactions'
// import { Transaction, TxAttrUsage } from '../transactions'
import { RPCClient, Query } from '../rpc'
// import { RPCClient, Query } from '../rpc'
import { modscan } from '../api'
import { TORRENT_SCRIPT_HASH, TORRENT_FUND_HASH, TORRENT_ESCROW_PRIV_KEY, ASSET_ID } from '../consts'
import { Fixed8, reverseHex } from '../utils'
import { networks, httpsOnly, timeout } from '../settings'
import logger from '../logging'
import {raceToSuccess} from './common'
import {ContractParam, createScript, ScriptBuilder, generateDeployScript} from '../sc'
// import { BigNumber } from 'bignumber.js'

const log = logger('api')
export const name = 'modscan'

var cachedRPC = null
/**
 * API Switch for MainNet and TestNet
 * @param {string} net - 'MainNet', 'TestNet', or custom module-wallet-db URL.
 * @return {string} URL of API endpoint.
 */
export const getAPIEndpoint = net => {
  if (networks[net]) return networks[net].extra.modscan
  return net
}

/**
 * Get balances of MOD and MODC for an address
 * @param {string} net - 'MainNet' or 'TestNet'.
 * @param {string} address - Address to check.
 * @return {Promise<Balance>} Balance of address
 */
export const getBalance = (net, address) => {
  const apiEndpoint = getAPIEndpoint(net)
  return axios.get(apiEndpoint + '/v1/address/balance/' + address).then(res => {
    var balance = res.data.data.balance
    const bal = new Balance(balance)

    log.info(`Retrieved Balance for ${address} from modscan ${net}`)
    return bal
  })
}

/**
 * Get the current height of the light wallet DB
 * @param {string} net - 'MainNet' or 'TestNet'.
 * @return {Promise<number>} Current height.
 */
export const getWalletDBHeight = net => {
  const apiEndpoint = getAPIEndpoint(net)
  return axios.get(apiEndpoint + '/v1/block/height').then(response => {
    return parseInt(response.data.block_height)
  })
}

/**
 * Get transaction history for an account
 * @param {string} net - 'MainNet' or 'TestNet'.
 * @param {string} address - Address to check.
 * @return {Promise<PastTransaction[]>} a list of PastTransaction
 */
export const getTransactionHistory = (net, address) => {
  const apiEndpoint = getAPIEndpoint(net)
  return axios
    .get(apiEndpoint + '/v1/address/history/' + address)
    .then(response => {
      log.info(`Retrieved History for ${address} from modscan ${net}`)
      return response.data.data.history.map(rawTx => {
        var assetType = 'MOD'
        if (rawTx.asset === '0x' + ASSET_ID['MOD']) {
          assetType = 'MOD'
        } else if (rawTx.asset === '0x' + ASSET_ID['MODC']) {
          assetType = 'MODC'
        } else if (rawTx.asset === '0x' + ASSET_ID['SSC']) {
          assetType = 'SSC'
        } else {
          assetType = rawTx.asset
        }

        var balance = 0
        var vouts = JSON.parse(rawTx.vout)
        vouts.forEach(vout => {
          if (vout.address !== rawTx._from) {
            balance += Number(vout.value)
          }
        })
        return {
          from: rawTx._from,
          to: rawTx._to,
          txType: rawTx.type,
          blockHeight: new Fixed8(rawTx.block_number),
          txid: rawTx.txid,
          asset: assetType,
          amount: balance,
          timestamp: rawTx.timestamp
        }
      })
    })
}

/**
 * Get transaction history for an account
 * @param {string} net - 'MainNet' or 'TestNet'.
 * @param {string} address - Address to check.
 * @param {string} offset - Offset
 * @param {string} limit - Limit
 * @return {Promise<PastTransaction[]>} a list of PastTransaction
 */
export const getTransactionHistoryOffset = (net, address, offset, limit, asset = 'MOD') => {
  const apiEndpoint = getAPIEndpoint(net)
  return axios
    .get(apiEndpoint + '/v1/address/history/' + address + '/' + offset + '/' + limit + '/' + asset)
    .then(response => {
      log.info(`Retrieved History for ${address} from modscan ${net}`)
      var body = response.data.data.history.body.map(rawTx => {
        var assetType = 'MOD'
        if (rawTx.asset === '0x' + ASSET_ID['MOD']) {
          assetType = 'MOD'
        } else if (rawTx.asset === '0x' + ASSET_ID['MODC']) {
          assetType = 'MODC'
        } else if (rawTx.asset === '0x' + ASSET_ID['SSC']) {
          assetType = 'SSC'
        } else {
          assetType = rawTx.asset
        }
        var balance = 0
        var vouts = JSON.parse(rawTx.vout)
        vouts.forEach(vout => {
          if (vout.address !== rawTx._from) {
            balance += Number(vout.value)
          }
        })
        return {
          from: rawTx._from,
          to: rawTx._to,
          txType: rawTx.type,
          blockHeight: new Fixed8(rawTx.block_number),
          txid: rawTx.txid,
          asset: assetType,
          amount: balance,
          timestamp: rawTx.timestamp
        }
      })
      var ret = {total: response.data.data.history.total, body: body}
      return ret
    })
}

/**
 * Returns an appropriate RPC endpoint retrieved from a ModScan endpoint.
 * @param {string} net - 'MainNet', 'TestNet' or a custom ModScan-like url.
 * @return {Promise<string>} - URL
 */
export const getRPCEndpoint = net => {
  const apiEndpoint = getAPIEndpoint(net)
  return axios.get(apiEndpoint + '/v1/nodes/rpc')
    .then(res => {
      var data = res.data.data.nodes
      let nodes = data.sort((a, b) => b.height - a.height)
      if (httpsOnly) nodes = nodes.filter(n => n.url.includes('https://'))
      if (nodes.length === 0) throw new Error('No eligible nodes found!')
      const heightThreshold = nodes[0].height - 1
      const goodNodes = nodes.filter(n => n.height >= heightThreshold)
      const urls = goodNodes.map(n => n.url)
      if (urls.includes(cachedRPC)) {
        return new RPCClient(cachedRPC).ping().then(num => {
          if (num <= timeout.ping) return cachedRPC
          cachedRPC = null
          return getRPCEndpoint(net)
        })
      }
      const clients = urls.map(u => new RPCClient(u))
      return raceToSuccess(clients.map(c => c.ping().then(_ => c.net)))
    })
    .then(fastestUrl => {
      cachedRPC = fastestUrl
      return fastestUrl
    })
}

/** DEPRECATED: Send an asset to an address
 * @param {string} net - 'MainNet', 'TestNet' or a custom ModScan-like url.
 * @param {string} toAddress - address to receive
 * @param {string} fromPrivKey - sender's private key.
 * @param {string} fromAddress - address to send
 * @param {number} assetAmounts - amount to send.
 * @return {Response|any}
*/
export const doSendMod = (net, toAddress, fromPrivKey, fromAddress, assetAmounts) => {
  var toScriptHash = getScriptHashFromAddress(toAddress)

  return modscan.getBalance(net, fromAddress)
    .then((balance) => {
      if (Number(balance.assets.MOD.balance) < Number(assetAmounts)) {
        throw new Error('Insufficient balance!')
      }
      const outputs = [{
        assetId: ASSET_ID['MOD'],
        value: assetAmounts,
        scriptHash: toScriptHash
      }]
      const tx = Transaction.createContractTx(balance, outputs)
      tx.sign(fromPrivKey)
      var rpcServer = new RPCClient(net)

      return rpcServer.sendRawTransaction(tx.serialize())
        .then((data) => {
          if (data === true) {
            return tx.hash
          } else {
            throw new Error('SendTx was failed')
          }
        })
        .catch((error) => {
          throw new Error(error)
        })
    })
    .catch((error) => {
      throw new Error(error)
    })
}

/**
 * Get information of Provider and Client from Smart Contract Storage.
 * @param {string} net - 'MainNet', 'TestNet' or a custom ModScan-like url.
 * @param {string} KeyHash - 'Provider Script Hash' or 'Client Script Hash' or 'File Contract Hash'.
 * @param {string} param - parameter to get info.
 * @return {Response|any}
 */
export const getParameter = (net, KeyHash, Param) => {
  const fromAddrScriptHash = ContractParam.byteArray(KeyHash, 'byteArray')
  const param = ContractParam.string(Param)
  var script = {
    scriptHash: TORRENT_SCRIPT_HASH,
    operation: 'GetParameter',
    args: ContractParam.array(fromAddrScriptHash, param)
  }

  const strScript = createScript(script)

  const sb = new ScriptBuilder(strScript)
  // const params = sb.toScriptParams()
  return Query.invokeScript(sb.str)
    .execute(net)
    .then((res) => {
      if (res.error) {
        throw new Error(res.error.message)
      }
      return res
    })
}

/**
 * Get information of Provider and Client from Smart Contract Storage.
 * @param {string} net - 'MainNet', 'TestNet' or a custom ModScan-like url.
 * @param {string} address - The address of 'Provider'.
 * @param {string} field - parameter to get info.
 * @return {Response|any}
 */
export const getProviderInfo = (net, address, field) => {
  var scriptHash = getScriptHashFromAddress(address)
  scriptHash = reverseHex(scriptHash)
  const fromAddrScriptHash = ContractParam.byteArray(scriptHash, 'byteArray')
  const param = ContractParam.string(field)
  var script = {
    scriptHash: TORRENT_SCRIPT_HASH,
    operation: 'GetParameter',
    args: ContractParam.array(fromAddrScriptHash, param)
  }

  const strScript = createScript(script)

  const sb = new ScriptBuilder(strScript)
  // const params = sb.toScriptParams()
  return Query.invokeScript(sb.str)
    .execute(net)
    .then((res) => {
      if (res.error) {
        throw new Error(res.error.message)
      }
      return res
    })
}

/**
 * Remove provider information from blockchain.
 * @param {string} net - 'MainNet', 'TestNet' or a custom ModScan-like url.
 * @param {Balance} balance - The Provider's balance.
 * @param {string} privKey - The private key of 'Provider'.
 * @param {string} address - The address of 'Provider'.
 * @return {Response|any}
 */
export const RemoveProviderInfo = (net, balance, privKey, address) => {
  var scriptHash = getScriptHashFromAddress(address)
  scriptHash = reverseHex(scriptHash)
  const fromAddrScriptHash = ContractParam.byteArray(scriptHash, 'byteArray')

  var script = {
    scriptHash: TORRENT_SCRIPT_HASH,
    operation: 'RemoveProvider',
    args: ContractParam.array(fromAddrScriptHash)
  }

  const strScript = createScript(script)

  const invocationTx = Transaction.createInvocationTx(balance, null, strScript, 0.0000001)

  invocationTx.sign(privKey)
  var rpcServer = new RPCClient(net)

  return rpcServer.sendRawTransaction(invocationTx.serialize())
    .then((data) => {
      return data
    })
    .catch((error) => {
      throw new Error(error)
    })
}

/**
 * Denied File Contract.
 * @param {string} net - 'MainNet', 'TestNet' or a custom ModScan-like url.
 * @param {Balance} balance - The Provider's balance.
 * @param {string} privKey - The private key of 'Provider'.
 * @param {string} fileContractHash - The address of 'Provider'.
 * @return {Response|any}
 */
export const deniedFileContract = (net, balance, privKey, fileContractHash) => {
  const pFileContractHash = ContractParam.byteArray(fileContractHash, 'byteArray')

  var script = {
    scriptHash: TORRENT_SCRIPT_HASH,
    operation: 'DeniedFileContract',
    args: ContractParam.array(pFileContractHash)
  }

  const strScript = createScript(script)

  const invocationTx = Transaction.createInvocationTx(balance, null, strScript, 0.0000001)

  invocationTx.sign(privKey)
  var rpcServer = new RPCClient(net)

  return rpcServer.sendRawTransaction(invocationTx.serialize())
    .then((data) => {
      return data
    })
    .catch((error) => {
      throw new Error(error)
    })
}

/**
 * Only provider can be call this method for changing the status of file contract.
 * @param {string} net - 'MainNet', 'TestNet' or a custom ModScan-like url.
 * @param {Balance} balance - The Provider's balance.
 * @param {string} privKey - The private key of 'Provider'.
 * @param {string} fileContractHash - The address of 'Provider'.
 * @return {Response|any}
 */
export const setStatusAsDownload = (net, balance, privKey, fileContractHash) => {
  const pFileContractHash = ContractParam.byteArray(fileContractHash, 'byteArray')

  var script = {
    scriptHash: TORRENT_SCRIPT_HASH,
    operation: 'SetStatusAsDownload',
    args: ContractParam.array(pFileContractHash)
  }

  const strScript = createScript(script)

  const invocationTx = Transaction.createInvocationTx(balance, null, strScript, 0.0000001)

  invocationTx.sign(privKey)
  var rpcServer = new RPCClient(net)

  return rpcServer.sendRawTransaction(invocationTx.serialize())
    .then((data) => {
      return data
    })
    .catch((error) => {
      throw new Error(error)
    })
}

/**
 * Get information of Provider and Client from Smart Contract Storage.
 * @param {string} net - 'MainNet', 'TestNet' or a custom ModScan-like url.
 * @param {Balance} balance - Account balance.
 * @param {string} privKey - Account private key.
 * @param {string} providerAddress - The address of 'Provider'.
 * @param {BigNumber} duration - timestamp.
 * @param {BigNumber} collateral - the price when provider fails.
 * @param {BigNumber} storagePrice - Storage price. Unit is Currency/TB.
 * @param {BigNumber} storageSize - Storage size. Unit is 1MB.
 * @param {BigNumber} downloadPrice - Download price. Unit is Currency/TB.
 * @param {BigNumber} uploadPrice - Upload price. Unit is Currency/TB.
 * @param {BigNumber} proofStorageMinDuration - Min duration between proof of strages.
 * @param {BigNumber} proofStorageMaxDuration - Max duration between proof of strages.
 * @param {BigNumber} failLimit - fail limit for proof of strage.
 * @return {Response|any}
 */
export const setProviderInfo = (net, balance, privKey, providerAddress, duration, collateral, storagePrice, storageSize, downloadPrice, uploadPrice, proofStorageMinDuration, proofStorageMaxDuration, failLimit) => {
  // Get the script hash from address
  var scriptHash = getScriptHashFromAddress(providerAddress)
  scriptHash = reverseHex(scriptHash)

  // Check the parameters

  // Make the params
  const providerScriptHash = ContractParam.byteArray(scriptHash, 'byteArray')
  const pDuration = ContractParam.integer(Number(duration))
  const pCollateral = ContractParam.integer(Number(collateral))
  const pStoragePrice = ContractParam.integer(storagePrice)
  const pStorageSize = ContractParam.integer(storageSize)
  const pDownloadPrice = ContractParam.integer(downloadPrice)
  const pUploadPrice = ContractParam.integer(uploadPrice)
  const pFailLimit = ContractParam.integer(Number(failLimit))
  const pProofStorageMinDuration = ContractParam.integer(Number(proofStorageMinDuration))
  const pProofStorageMaxDuration = ContractParam.integer(Number(proofStorageMaxDuration))

  var script = {
    scriptHash: TORRENT_SCRIPT_HASH,
    operation: 'ProviderInfo',
    args: ContractParam.array(providerScriptHash, pDuration, pCollateral, pStoragePrice, pStorageSize, pDownloadPrice, pUploadPrice, pProofStorageMinDuration, pProofStorageMaxDuration, pFailLimit)
  }
  const strScript = createScript(script)

  // const sb = new ScriptBuilder(strScript)

  const invocationTx = Transaction.createInvocationTx(balance, null, strScript, 5000)

  invocationTx.sign(privKey)
  var rpcServer = new RPCClient(net)

  return rpcServer.sendRawTransaction(invocationTx.serialize())
    .then((data) => {
      return data
    })
    .catch((error) => {
      throw new Error(error)
    })
}

/** Register the File Contract on Blockchain and make an contract between provider and client
 * @param {string} net - 'MainNet', 'TestNet' or a custom ModScan-like url.
 * @param {Balance} balance - Account balance.
 * @param {string} privKey - Account private key.
 * @param {string} clientAddress - The address of 'Client'.
 * @param {string} providerAddress - The address of 'Provider'.
 * @param {string} merkleTreeHash - The Merkle Tree Hash of File.
 * @param {BigNumber} duration - The File Contract duration.
 * @param {BigNumber} fileSize - The File size.
 * @param {BigNumber} version - File Contract Version.
 * @param {BigNumber} splitFileCount - split file count.
 * @param {BigNumber} depositAmount - deposit amount.
 * @param {string} magnetAddress - File's magnet address.
 * @param {BigNumber} timestamp - Timestamp
 * @param {string} orgin - Original data
 * @return {Response|any}
*/
export const setFileContract = (net, balance, privKey, clientAddress, providerAddress, merkleTreeHash, duration, fileSize, version, splitFileCount, depositAmount, magnetAddress, timestamp, origin) => {
  // Get the script hash from address
  var clientScriptHash = getScriptHashFromAddress(clientAddress)
  clientScriptHash = reverseHex(clientScriptHash)

  var providerScriptHash = getScriptHashFromAddress(providerAddress)
  providerScriptHash = reverseHex(providerScriptHash)

  // Check the parameters

  if (Number(depositAmount) <= 0) {
    throw new Error('Deposit Amount is not sufficient.')
  }

  // Make the params
  const pClientScriptHash = ContractParam.byteArray(clientScriptHash, 'byteArray')
  const pProviderScriptHash = ContractParam.byteArray(providerScriptHash, 'byteArray')
  const pMerkleTreeHash = ContractParam.byteArray(merkleTreeHash, 'byteArray')

  const pDuration = ContractParam.integer(Number(duration))
  const pFileSize = ContractParam.integer(Number(fileSize))
  const pVersion = ContractParam.integer(Number(version))
  const pSplitFileCount = ContractParam.integer(Number(splitFileCount))
  const pMagnetAddress = ContractParam.string(magnetAddress)
  const pTimestamp = ContractParam.integer(Number(timestamp))
  const pOrigin = ContractParam.string(origin)
  var script = {
    scriptHash: TORRENT_SCRIPT_HASH,
    operation: 'FileContract',
    args: ContractParam.array(pClientScriptHash, pProviderScriptHash, pMerkleTreeHash, pDuration, pFileSize, pVersion, pSplitFileCount, pMagnetAddress, pTimestamp, pOrigin)
  }

  const strScript = createScript(script)
  // const sb = new ScriptBuilder(strScript)

  const outputs = [{
    assetId: ASSET_ID['MOD'],
    value: Number(depositAmount),
    scriptHash: TORRENT_FUND_HASH
  }]

  const invocationTx = Transaction.createInvocationTx(balance, outputs, strScript, 1) // Normal 9

  invocationTx.sign(privKey)

  var rpcServer = new RPCClient(net)

  return rpcServer.sendRawTransaction(invocationTx.serialize())
    .then((data) => {
      return data
    })
    .catch((error) => {
      throw new Error(error)
    })
}

/** Deposit collateral from Provider
 * @param {string} net - 'MainNet', 'TestNet' or a custom ModScan-like url.
 * @param {Balance} balance - Account balance to deposit.
 * @param {string} privKey - Account private key.
 * @param {string} providerAddress - The address of 'Provider'.
 * @param {BigNumber} depositAmount - The amount to deposit.
 * @return {Response|any}
*/
export const depositCollateral = (net, balance, privKey, providerAddress, depositAmount) => {
  // Get the script hash from address
  var providerScriptHash = getScriptHashFromAddress(providerAddress)
  providerScriptHash = reverseHex(providerScriptHash)

  // Check the parameters

  // Make the params
  const pProviderScriptHash = ContractParam.byteArray(providerScriptHash, 'byteArray')

  var script = {
    scriptHash: TORRENT_SCRIPT_HASH,
    operation: 'DepositCollateral',
    args: ContractParam.array(pProviderScriptHash)
  }

  const strScript = createScript(script)

  // const sb = new ScriptBuilder(strScript)

  const outputs = [{
    assetId: ASSET_ID['MOD'],
    value: Number(depositAmount),
    scriptHash: TORRENT_FUND_HASH
  }]

  const invocationTx = Transaction.createInvocationTx(balance, outputs, strScript, 1)

  invocationTx.sign(privKey)

  var rpcServer = new RPCClient(net)

  return rpcServer.sendRawTransaction(invocationTx.serialize())
    .then((data) => {
      return data
    })
    .catch((error) => {
      throw new Error(error)
    })
}

/** When Provider receives a filecontract then he can do accept this.
 * @param {string} net - 'MainNet', 'TestNet' or a custom ModScan-like url.
 * @param {Balance} balance - Account balance.
 * @param {string} privKey - Account private key.
 * @param {string} fileContractHash - The hash of 'File Contract'.
 * @param {string} providerAddress - The address of 'Provider'.
 * @return {Response|any}
*/
export const acceptFileContract = (net, balance, privKey, fileContractHash, providerAddress) => {
  // Get the script hash from address
  var providerScriptHash = getScriptHashFromAddress(providerAddress)
  providerScriptHash = reverseHex(providerScriptHash)

  // Check the parameters

  // Make the params
  const pFileContractHash = ContractParam.byteArray(fileContractHash, 'byteArray')
  const pProviderScriptHash = ContractParam.byteArray(providerScriptHash, 'byteArray')

  var script = {
    scriptHash: TORRENT_SCRIPT_HASH,
    operation: 'AcceptFileContract',
    args: ContractParam.array(pFileContractHash, pProviderScriptHash)
  }

  const strScript = createScript(script)

  // const sb = new ScriptBuilder(strScript)

  const invocationTx = Transaction.createInvocationTx(balance, null, strScript, 0.0000001)

  invocationTx.sign(privKey)

  var rpcServer = new RPCClient(net)

  return rpcServer.sendRawTransaction(invocationTx.serialize())
    .then((data) => {
      return data
    })
    .catch((error) => {
      throw new Error(error)
    })
}

/** When a user want to check his status, then he can call this function.
 * @param {string} net - 'MainNet', 'TestNet' or a custom Modscan-like url.
 * @param {string} fileContractHash - The File contract hash to check the status.
 * @return {Response|any}
 */
export const isFinishedFileContract = (net, fileContractHash) => {
  // Check the parameters

  // Make the params
  const pFileContractHash = ContractParam.byteArray(fileContractHash, 'byteArray')

  var script = {
    scriptHash: TORRENT_SCRIPT_HASH,
    operation: 'IsFinishedFileContract',
    args: ContractParam.array(pFileContractHash)
  }

  const strScript = createScript(script)
  const sb = new ScriptBuilder(strScript)
  // const params = sb.toScriptParams()
  return Query.invokeScript(sb.str)
    .execute(net)
    .then((res) => {
      if (res.error) {
        throw new Error(res.error.message)
      }
      return res
    })
}

/** When a Provider or Client want to change the status of file contract.
 * @param {string} net - 'MainNet', 'TestNet' or a custom Modscan-like url.
 * @param {Balance} balance - Account balance.
 * @param {string} privKey - Account private key.
 * @param {string} fileContractHash - The File contract hash to check the status.
 * @return {Response|any}
 */
export const CheckFileContract = (net, balance, privKey, fileContractHash) => {
  // Check the parameters

  // Make the params
  const pFileContractHash = ContractParam.byteArray(fileContractHash, 'byteArray')

  var script = {
    scriptHash: TORRENT_SCRIPT_HASH,
    operation: 'CheckFileContract',
    args: ContractParam.array(pFileContractHash)
  }

  const strScript = createScript(script)

  // const sb = new ScriptBuilder(strScript)

  const invocationTx = Transaction.createInvocationTx(balance, null, strScript, 0.0000001)

  invocationTx.sign(privKey)
  var rpcServer = new RPCClient(net)

  return rpcServer.sendRawTransaction(invocationTx.serialize())
    .then((data) => {
      return data
    })
    .catch((error) => {
      throw new Error(error)
    })
}

/** Withdraw coin from torrent escrow.
 * @param {string} net - 'MainNet', 'TestNet' or a custom ModScan-like url.
 * @param {string} toAddress - withdraw account.
 * @param {string} amount - withdraw amount.
 * @return {Response|any}
*/
export const WithdrawCoinFromTorrentEscrow = (net, toAddress, amount) => {
  var escrowAddress = getAddressFromScriptHash(TORRENT_SCRIPT_HASH)
  var toScriptHash = getScriptHashFromAddress(toAddress)

  // var depositError = new Promise(function(error) {
  //  throw error
  // })

  return modscan.getBalance(net, escrowAddress)
    .then((balance) => {
      const bal = new Balance(balance)
      const outputs = [{
        assetId: ASSET_ID['MOD'],
        value: amount,
        scriptHash: toScriptHash
      }]
      const tx = Transaction.createContractTx(bal, outputs)
      tx.signTorrent(TORRENT_ESCROW_PRIV_KEY)
      var rpcServer = new RPCClient(net)

      return rpcServer.sendRawTransaction(tx.serialize())
        .then((data) => {
          console.log(data)
          return data
        })
        .catch((error) => {
          throw new Error(error)
        })
    })
    .catch((error) => {
      throw new Error(error)
    })
}

/** Get escrow info from address
 * @param {string} net - 'MainNet', 'TestNet' or a custom ModScan-like url.
 * @param {string} address - The address to get esrow info.
 * @return {Response|any}
*/
export const getEscrowInfos = (net, address) => {
  var result = {
    CollateralBudget: 0,
    LockBalance: 0,
    WithdrawHold: 0
  }

  var scriptHash = getScriptHashFromAddress(address)
  scriptHash = reverseHex(scriptHash)
  const addrScriptHash = ContractParam.byteArray(scriptHash, 'byteArray')
  const pCollateralBudget = ContractParam.string('CollateralBudget')
  const pLockBalance = ContractParam.string('LockBalance')
  const pwithdrawHold = ContractParam.string('withdrawHold')

  var script = {
    scriptHash: TORRENT_SCRIPT_HASH,
    operation: 'GetParameter',
    args: ContractParam.array(addrScriptHash, pCollateralBudget)
  }

  const strScript = createScript(script)
  const sb = new ScriptBuilder(strScript)

  return Query.invokeScript(sb.str)
    .execute(net)
    .then((res) => {
      if (res.error) {
        throw new Error(res.error.message)
      }
      result.CollateralBudget = parseInt(reverseHex(res.result.stack[0].value), 16)
      var scriptLock = {
        scriptHash: TORRENT_SCRIPT_HASH,
        operation: 'GetParameter',
        args: ContractParam.array(addrScriptHash, pLockBalance)
      }
      const strScriptLock = createScript(scriptLock)
      const sbLock = new ScriptBuilder(strScriptLock)
      return Query.invokeScript(sbLock.str)
        .execute(net)
        .then((resLock) => {
          if (resLock.error) {
            throw new Error(resLock.error.message)
          }
          result.LockBalance = parseInt(reverseHex(resLock.result.stack[0].value), 16)
          var scriptWithdraw = {
            scriptHash: TORRENT_SCRIPT_HASH,
            operation: 'GetParameter',
            args: ContractParam.array(addrScriptHash, pwithdrawHold)
          }
          const strScriptWithdraw = createScript(scriptWithdraw)
          const sbWithdraw = new ScriptBuilder(strScriptWithdraw)

          return Query.invokeScript(sbWithdraw.str)
            .execute(net)
            .then((resWithdraw) => {
              if (resWithdraw.error) {
                throw new Error(resWithdraw.error.message)
              }
              result.WithdrawHold = parseInt(reverseHex(resWithdraw.result.stack[0].value), 16)
              return result
            })
        })
    })
}

/** Provider must send Proof of Storage to blockchain.
 * @param {string} net - 'MainNet', 'TestNet' or a custom ModScan-like url.
 * @param {string} privKey - Account private key.
 * @param {Balance} balance - balance of the account.
 * @param {string} fileContractHash - The hash of 'File Contract'.
 * @param {string} providerAddress - The address of 'Provider'.
 * @param {string} merkleData - The hash of Merkle Tree.
 * @return {Response|any}
*/
export const proofOfStorage = (net, privKey, balance, fileContractHash, providerAddress, merkleData) => {
  // Get the script hash from address
  var scriptHash = getScriptHashFromAddress(providerAddress)
  scriptHash = reverseHex(scriptHash)

  // Check the parameters

  // Make the params
  const pProviderScriptHash = ContractParam.byteArray(scriptHash, 'byteArray')
  const pFileContractHash = ContractParam.byteArray(fileContractHash, 'byteArray')
  const pMerkleData = ContractParam.byteArray(merkleData, 'byteArray')

  var script = {
    scriptHash: TORRENT_SCRIPT_HASH,
    operation: 'ProofOfStorage',
    args: ContractParam.array(pFileContractHash, pProviderScriptHash, pMerkleData)
  }

  const strScript = createScript(script)

  const invocationTx = Transaction.createInvocationTx(balance, null, strScript, 0.0000001)

  invocationTx.sign(privKey)
  var rpcServer = new RPCClient(net)

  return rpcServer.sendRawTransaction(invocationTx.serialize())
    .then((data) => {
      return data
    })
    .catch((error) => {
      throw new Error(error)
    })
}

/** Transfer coins from collateral to withdraw
 * @param {net} net - 'MainNet', 'TestNet' or a custom ModScan-like url.
 * @param {string} privKey - Account private key.
 * @param {Balance} balance - balance of the account.
 * @param {string} address - The address to transfer.
 * @param {Fixed8} amount - Transfer amount.
 * @return {Response|any}
*/
export const transferFromCollateralToWithdraw = (net, privKey, balance, address, amount) => {
  // Get the script hash from address
  var scriptHash = getScriptHashFromAddress(address)
  scriptHash = reverseHex(scriptHash)

  // Check the parameters

  // Make the params
  const pProviderScriptHash = ContractParam.byteArray(scriptHash, 'byteArray')
  const pAmount = ContractParam.integer(amount)

  var script = {
    scriptHash: TORRENT_SCRIPT_HASH,
    operation: 'TransferFromCollateralToWithdraw',
    args: ContractParam.array(pProviderScriptHash, pAmount)
  }

  const strScript = createScript(script)

  const invocationTx = Transaction.createInvocationTx(balance, null, strScript, 0.0000001)

  invocationTx.sign(privKey)
  var rpcServer = new RPCClient(net)

  return rpcServer.sendRawTransaction(invocationTx.serialize())
    .then((data) => {
      return data
    })
    .catch((error) => {
      throw new Error(error)
    })
}

/** Transfer coins from withdraw to collateral
 * @param {net} net - 'MainNet', 'TestNet' or a custom ModScan-like url.
 * @param {string} privKey - Account private key.
 * @param {Balance} balance - balance of the account.
 * @param {string} address - The address to transfer.
 * @param {Fixed8} amount - Transfer amount.
 * @return {Response|any}
*/
export const transferFromWithdrawToCollateral = (net, privKey, balance, address, amount) => {
  // Get the script hash from address
  var scriptHash = getScriptHashFromAddress(address)
  scriptHash = reverseHex(scriptHash)

  // Check the parameters

  // Make the params
  const pProviderScriptHash = ContractParam.byteArray(scriptHash, 'byteArray')
  const pAmount = ContractParam.integer(amount)

  var script = {
    scriptHash: TORRENT_SCRIPT_HASH,
    operation: 'TransferFromWithdrawToCollateral',
    args: ContractParam.array(pProviderScriptHash, pAmount)
  }

  const strScript = createScript(script)

  const invocationTx = Transaction.createInvocationTx(balance, null, strScript, 0.0000001)

  invocationTx.sign(privKey)
  var rpcServer = new RPCClient(net)

  return rpcServer.sendRawTransaction(invocationTx.serialize())
    .then((data) => {
      return data
    })
    .catch((error) => {
      throw new Error(error)
    })
}

/** This contract will be called when a client want to download thier file from Provider.
 * @param {string} net - 'MainNet', 'TestNet' or a custom ModScan-like url.
 * @return {Response|any}
*/
// export function downloadContract(net)
// {
//
// }

export const deploySmartContract = (net, fromAddress, privKey, script, paramList, returnType, needStorage, name, version, author, email, description, modl) => {
  return getBalance(net, fromAddress)
    .then((data) => {
      try {
        const balance = new Balance(data)
        const sb = generateDeployScript({
          script: script,
          name: name,
          version: version,
          author: author,
          email: email,
          description: description,
          needsStorage: needStorage,
          returnType: returnType,
          paramaterList: paramList
        })
        const invocationTx = Transaction.createInvocationTx(balance, null, sb.str, modl)
        invocationTx.sign(privKey)
        var rpcServer = new RPCClient(net)
        return rpcServer.sendRawTransaction(invocationTx.serialize())
          .then((data) => {
            var returnData = {status: data, txHash: invocationTx.hash}
            return returnData
          })
          .catch((error) => {
            throw new Error(error)
          })
      } catch (ex) {
        throw new Error(ex)
      }
    })
    .catch((error) => {
      throw new Error(error)
    })
}

/** Deploy MST-1 Token */
export const deployMST1Token = (net, balance, privKey, contractScriptHash) => {
  var script = {
    scriptHash: contractScriptHash,
    operation: 'deploy',
    args: ContractParam.array([])
  }

  const strScript = createScript(script)

  const invocationTx = Transaction.createInvocationTx(balance, null, strScript, 5)

  invocationTx.sign(privKey)

  var rpcServer = new RPCClient(net)

  return rpcServer.sendRawTransaction(invocationTx.serialize())
    .then((data) => {
      return data
    })
    .catch((error) => {
      throw new Error(error)
    })
}
